import React, { useEffect, useRef, useState } from 'react';
import { describeOverlapTarget, SEVERITY_NOTE } from '../../utils/classOverlapFix';
import '../../components/chat/batch/ClassCheckPanel.css';

const VISIBLE_STUDENTS = 5;

/**
 * 검사기 다중 검사 결과 위 반 점검. 겹치는 문장마다 한 명은 두고 나머지 학생만 '고칠 학생'으로 보여 준다.
 * 행을 누르면 그 학생 검사 결과로 이동한다.
 */
const InspectorClassCheck = ({ plan, names = [], selectedIndex = null, approximate = false, onSelect, onShown }) => {
  const [showAll, setShowAll] = useState(false);
  const shownKeyRef = useRef('');
  const summary = plan?.summary;
  const targets = plan?.targets || [];

  useEffect(() => {
    if (!summary || summary.studentCount < 2) return;
    const key = `${summary.studentCount}:${summary.flaggedStudents}:${summary.sameGroups}:${summary.nearGroups}`;
    if (shownKeyRef.current === key) return;
    shownKeyRef.current = key;
    onShown?.(plan);
  }, [onShown, plan, summary]);

  if (!summary || summary.studentCount < 2) return null;

  if (targets.length === 0) {
    return (
      <div className="class-check is-clean" data-testid="inspector-class-check">
        <strong>학생마다 다른 문장으로 써졌어요</strong>
        <span>반 전체에서 같은 문장·거의 같은 문장이 없어요.</span>
      </div>
    );
  }

  const groupCount = summary.sameGroups + summary.nearGroups;
  const visible = showAll ? targets : targets.slice(0, VISIBLE_STUDENTS);
  const note = SEVERITY_NOTE[summary.severity];

  return (
    <section className="class-check" data-testid="inspector-class-check">
      <div className="class-check-head">
        <div className="class-check-title">
          <strong>다른 학생과 겹치는 문장 {groupCount}곳</strong>
          <span>
            {summary.sameGroups > 0 && `같은 문장 ${summary.sameGroups}`}
            {summary.sameGroups > 0 && summary.nearGroups > 0 && ' · '}
            {summary.nearGroups > 0 && `거의 같은 문장 ${summary.nearGroups}`}
          </span>
        </div>
      </div>
      {note && <p className="class-check-note">{note}</p>}
      <p className="class-check-sub is-left">
        겹치는 문장마다 한 명은 그대로 두고, 아래 {targets.length}명만 고치면 돼요.
        {approximate && ' 스캔·사진은 글자 인식 오차가 있어 거의 같은 문장으로 보여 드려요.'}
      </p>
      <ul className="class-check-list">
        {visible.map((target) => (
          <li key={target.index} className={selectedIndex === target.index ? 'is-selected' : ''}>
            <div className="class-check-student">
              <span className="class-check-name">{names[target.index] || `${target.index + 1}번 학생`}</span>
              <span className="class-check-reason">{describeOverlapTarget(target)}</span>
            </div>
            <button
              type="button"
              data-testid={`inspector-class-check-open-${target.index}`}
              disabled={selectedIndex === target.index}
              onClick={() => onSelect?.(target.index)}
            >
              {selectedIndex === target.index ? '보는 중' : '보기'}
            </button>
          </li>
        ))}
      </ul>
      {targets.length > VISIBLE_STUDENTS && (
        <button type="button" className="class-check-more" onClick={() => setShowAll((value) => !value)}>
          {showAll ? '접기' : `${targets.length - VISIBLE_STUDENTS}명 더 보기`}
        </button>
      )}
    </section>
  );
};

/** 선택한 학생 결과 위에 붙는 한 줄 안내: 바꿀 문장을 그대로 보여 준다. */
export const InspectorOverlapSentences = ({ target }) => {
  if (!target) return null;
  return (
    <div className="class-check-sentences" data-testid="inspector-overlap-sentences">
      <strong>다른 학생 글과 겹치는 문장이에요</strong>
      <ul>
        {target.sentences.slice(0, 6).map((sentence) => (
          <li key={`${sentence.start}-${sentence.clusterId}`}>
            <span>{sentence.text}</span>
            <em>{sentence.people - 1}명과 {sentence.tier === 'same' ? '같아요' : '거의 같아요'}</em>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default InspectorClassCheck;
